<template>
  <div class="update-avatar">
    <div class="update-avatar__status row">
      <div class="medium-12 columns">
        <p>
          <strong>{{ $t('INTEGRATION_APPS.UPDATE_AVATAR.STATUS') }}:</strong>
          <span :class="badgeClass">{{ statusLabel }}</span>
        </p>
        <p v-if="settings.phone">
          <strong>{{ $t('INTEGRATION_APPS.UPDATE_AVATAR.PHONE') }}:</strong>
          {{ settings.phone }}
          <span v-if="settings.pushname">({{ settings.pushname }})</span>
        </p>
        <p v-if="settings.last_success_at">
          <strong>{{ $t('INTEGRATION_APPS.UPDATE_AVATAR.LAST_SUCCESS') }}:</strong>
          {{ settings.last_success_at }}
        </p>
        <p v-if="settings.last_error">
          <strong>{{ $t('INTEGRATION_APPS.UPDATE_AVATAR.LAST_ERROR') }}:</strong>
          {{ settings.last_error }}
        </p>
        <p class="update-avatar__url">
          <strong>service_url:</strong> {{ serviceUrl }}
        </p>
      </div>
    </div>

    <div class="row">
      <div class="medium-12 columns button-group">
        <woot-button
          :loading="busy"
          :disabled="!serviceUrl || busy"
          @click="connect"
        >
          {{ $t('INTEGRATION_APPS.UPDATE_AVATAR.CONNECT') }}
        </woot-button>
        <woot-button
          class="nice alert"
          :loading="busy"
          :disabled="!serviceUrl || busy"
          @click="disconnect"
        >
          {{ $t('INTEGRATION_APPS.UPDATE_AVATAR.DISCONNECT') }}
        </woot-button>
      </div>
    </div>

    <div v-if="qrcode" class="row update-avatar__qr">
      <div class="column text-center">
        <p>{{ $t('INTEGRATION_APPS.UPDATE_AVATAR.QR_HINT') }}</p>
        <qrcode-vue :value="qrcode" :size="280" level="H" />
      </div>
    </div>
  </div>
</template>

<script>
import QrcodeVue from 'qrcode.vue';
import { io } from 'socket.io-client';
import alertMixin from 'shared/mixins/alertMixin';

export default {
  name: 'UpdateAvatar',
  components: { QrcodeVue },
  mixins: [alertMixin],
  props: {
    integration: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      busy: false,
      qrcode: null,
      socket: null,
      liveStatus: null,
    };
  },
  computed: {
    hook() {
      return this.integration.hooks?.[0] || {};
    },
    settings() {
      return {
        ...(this.hook.settings || {}),
        ...(this.liveStatus || {}),
      };
    },
    serviceUrl() {
      const url = this.settings.service_url || '';
      return url.replace(/\/$/, '');
    },
    connected() {
      return !!this.settings.connected;
    },
    working() {
      return this.settings.working !== false;
    },
    statusLabel() {
      if (!this.connected) {
        return this.$t('INTEGRATION_APPS.UPDATE_AVATAR.BADGE_DISCONNECTED');
      }
      if (!this.working) {
        return this.$t('INTEGRATION_APPS.UPDATE_AVATAR.BADGE_DEGRADED');
      }
      return this.$t('INTEGRATION_APPS.UPDATE_AVATAR.BADGE_CONNECTED');
    },
    badgeClass() {
      if (!this.connected) return 'badge badge--disconnected';
      if (!this.working) return 'badge badge--degraded';
      return 'badge badge--connected';
    },
  },
  beforeDestroy() {
    this.teardownSocket();
  },
  methods: {
    authHeaders() {
      const token = this.settings.service_token;
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['x-avatar-service-token'] = token;
      return headers;
    },
    teardownSocket() {
      if (this.socket) {
        this.socket.disconnect();
        this.socket = null;
      }
    },
    listenSocket(sessionId) {
      this.teardownSocket();
      if (!this.serviceUrl || !sessionId) return;

      this.socket = io(this.serviceUrl, { transports: ['websocket', 'polling'] });

      this.socket.on(`${sessionId}-qrcode-generated`, payload => {
        this.qrcode = typeof payload === 'string' ? payload : payload?.qr;
      });

      this.socket.on(`${sessionId}-client-connected`, payload => {
        this.liveStatus = {
          connected: true,
          working: true,
          phone: payload?.phone,
          pushname: payload?.pushname,
        };
        this.qrcode = null;
        this.persistSettings({
          connected: true,
          working: true,
          phone: payload?.phone,
          pushname: payload?.pushname,
          session_id: sessionId,
          last_connected_at: new Date().toISOString(),
        });
        this.showAlert(this.$t('INTEGRATION_APPS.UPDATE_AVATAR.CONNECTED_OK'));
      });

      this.socket.on(`${sessionId}-client-disconnected`, () => {
        this.liveStatus = { connected: false, working: false };
        this.persistSettings({
          connected: false,
          working: false,
          last_disconnected_at: new Date().toISOString(),
        });
      });

      this.socket.on(`${sessionId}-status`, status => {
        this.liveStatus = {
          connected: !!status?.connected,
          working: status?.working !== false,
          phone: status?.phone,
          pushname: status?.pushname,
          blocked: !!status?.blocked,
          last_success_at: status?.last_success_at,
          last_error: status?.last_error,
        };
      });
    },
    async persistSettings(partial) {
      if (!this.hook.id) return;
      const next = { ...(this.hook.settings || {}), ...partial };
      try {
        await this.$store.dispatch('integrations/updateHook', {
          hookId: this.hook.id,
          data: { settings: next },
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    },
    async connect() {
      if (!this.serviceUrl) {
        this.showAlert(this.$t('INTEGRATION_APPS.UPDATE_AVATAR.MISSING_URL'));
        return;
      }
      this.busy = true;
      try {
        const response = await fetch(`${this.serviceUrl}/api/v1/session/reconnect`, {
          method: 'POST',
          headers: this.authHeaders(),
          body: JSON.stringify({}),
        });
        const body = await response.json();
        if (!response.ok) {
          throw new Error(body?.message || 'reconnect_failed');
        }
        const sessionId = body?.data?.session_id || this.settings.session_id;
        this.qrcode = body?.data?.qr || null;
        this.listenSocket(sessionId);
        await this.persistSettings({ session_id: sessionId });
        this.showAlert(this.$t('INTEGRATION_APPS.UPDATE_AVATAR.QR_HINT'));
      } catch (error) {
        this.showAlert(this.$t('INTEGRATION_APPS.UPDATE_AVATAR.CONNECT_ERROR'));
      } finally {
        this.busy = false;
      }
    },
    async disconnect() {
      if (!this.serviceUrl) return;
      this.busy = true;
      try {
        await fetch(`${this.serviceUrl}/api/v1/session/logout`, {
          method: 'POST',
          headers: this.authHeaders(),
          body: JSON.stringify({}),
        });
        this.teardownSocket();
        this.qrcode = null;
        this.liveStatus = { connected: false, working: false };
        await this.persistSettings({
          connected: false,
          working: false,
          last_disconnected_at: new Date().toISOString(),
        });
        this.showAlert(this.$t('INTEGRATION_APPS.UPDATE_AVATAR.DISCONNECTED_OK'));
      } catch (error) {
        this.showAlert(this.$t('INTEGRATION_APPS.UPDATE_AVATAR.DISCONNECT_ERROR'));
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>

<style scoped>
.update-avatar {
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid var(--s-100);
}
.update-avatar__qr {
  margin-top: 1.5rem;
}
.button-group {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}
.badge {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border-radius: 4px;
  font-weight: 600;
}
.badge--connected {
  background: #d1fae5;
  color: #065f46;
}
.badge--disconnected {
  background: #fee2e2;
  color: #991b1b;
}
.badge--degraded {
  background: #ffedd5;
  color: #9a3412;
}
.update-avatar__url {
  word-break: break-all;
  opacity: 0.8;
}
</style>
